// [목적] Settings 풀세트 공용 헤더(SetHeader: 뒤로 + 타이틀 + 우측 슬롯)와 공유 토큰 상수(ST_*)
// [이유] W2(design-alignment): 하드코딩 쿨톤 export 상수(ST_TEXT_HI/#14181F 등)를
//         paper/ink/line/brand 정본 토큰으로 교체. R-9: "SetHeader 1:1 포팅 SoT" → "정본=paper/ink/line".
// [사이드 이팩트] 모든 settings sub-page 가 ST_* 상수를 import 하여 사용 — 일괄 정본화됨.
// [수정 시 고려사항] 다크모드 도입 시 색상 토큰 분기 추가.
//                   pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.

import { Box, ButtonBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// [목적] paper/ink/line/brand 정본 토큰 export 상수 — settings sub-page 공용 SoT (R-9)
// [이유] ADR-TR8: 신규 토큰 추가 없이 paper/ink/line/brand/accent 만 사용. [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         ST_TEXT_HI: ink.high(#29261B) ← 쿨톤 #14181F
//         ST_TEXT_MID: ink.mid(#5C5849) ← 쿨톤 #586069
//         ST_TEXT_LOW: ink.low(#6F6A5C) ← 쿨톤 #8B949E — 보조 텍스트 한정(비본문)
//         ST_BORDER: line.base(#E8E2D5) ← 쿨톤 #E1E7EF
//         ST_BG1: paper[1](#FAF9F7) ← 쿨톤 #F6F8FB
//         ST_BRAND: brand[500] → 토큰 참조 전환(값 동일 #1F6FEB)
//         ST_GREEN: accent[500] → 토큰 참조 전환(값 동일 #2BB673)
// [사이드 이팩트] ST_* 를 import 하는 모든 settings sub-page 가 자동으로 warm 톤 적용됨
// [수정 시 고려사항] ink.low(#6F6A5C) 는 본문 텍스트 미사용 — 보조 정보(힌트/설명) 한정(ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//                   contrast.spec.ts 에서 paper[1] 위 ink.high/mid 4.5:1 이상 게이트 검증됨.
const _c = tokens.color;
const _L = 'light' as const;
export const ST_TEXT_HI = _c.ink.high[_L]; // #29261B (← 쿨톤 #14181F)
export const ST_TEXT_MID = _c.ink.mid[_L]; // #5C5849 (← 쿨톤 #586069)
export const ST_TEXT_LOW = _c.ink.low[_L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
export const ST_BORDER = _c.line.base[_L]; // #E8E2D5 (← 쿨톤 #E1E7EF)
export const ST_BG1 = _c.paper[1][_L]; // #FAF9F7 (← 쿨톤 #F6F8FB)
export const ST_BRAND = _c.brand[500][_L]; // #1F6FEB (하드코딩 → 토큰 참조)
export const ST_GREEN = _c.accent[500][_L]; // #2BB673 (하드코딩 → 토큰 참조)
// [2026-09-18] SettingsPage.tsx "회원 탈퇴" 행이 raw hex '#E53E3E'(4.13:1, AA 미달)를 직접 입력하고
// 있었다(§6-3 위반 + docs/qa/2026-09-17_contrast-measure.md). danger[500](#D73A49, 흰 배경 위 4.57:1)로 교체.
export const ST_DANGER = _c.danger[500][_L];
// [2026-09-20] 카드/토글 knob 등 흰 표면 — paper[0](#FFFFFF). SettingsPage 카드 개편에서 raw '#fff' 대체(§6-3)
export const ST_SURFACE = _c.paper[0][_L];

/**
 * [목적] OAuth provider 식별자 → 사용자 친화적 라벨 변환 헬퍼 (3개 설정 화면 공통)
 * [이유] MSC-FE-01: SettingsPage/SettingsSubPages 에서 동일 매핑 중복 제거.
 *         primaryProvider=null/미수신 시 카카오 오표시 금지 — 중립 텍스트("소셜 계정") 반환.
 * [사이드 이팩트] SettingsPage(accountSubLine)/SettingsSubPages(PasswordScreen) 소비.
 *                 새 IdP 추가 시 map 객체 + BE me.dto primaryProvider enum 동시 갱신.
 * [수정 시 고려사항] suffix 파라미터로 "연결됨" / "" 등 문맥별 조합 가능.
 *                   primaryProvider=null 이면 suffix 미표시 — 중립 문자열 반환.
 */
export function providerLabel(
  primary: 'kakao' | 'naver' | 'google' | 'apple' | null | undefined,
  suffix = '',
): string {
  if (primary == null) return '소셜 계정';
  const map: Record<string, string> = {
    kakao: '카카오',
    naver: '네이버',
    google: 'Google',
    apple: 'Apple',
  };
  const name = map[primary] ?? primary;
  return suffix ? `${name} ${suffix}` : name;
}

export function SetHeader({
  title,
  right,
  fallbackTo = '/settings',
}: {
  title: string;
  right?: ReactNode;
  fallbackTo?: string;
}): JSX.Element {
  const navigate = useNavigate();
  const handleBack = (): void => {
    if (window.history.length > 1) navigate(-1);
    else navigate(fallbackTo);
  };
  return (
    <Box
      component="header"
      sx={{
        bgcolor: _c.paper[0][_L],
        borderBottom: `1px solid ${ST_BORDER}`,
        p: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <ButtonBase
        onClick={handleBack}
        aria-label="뒤로"
        sx={{
          width: 30,
          height: 30,
          fontSize: 18,
          color: ST_TEXT_HI,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ‹
      </ButtonBase>
      <Typography
        component="h1"
        sx={{
          flex: 1,
          fontSize: 15,
          fontWeight: 800,
          color: ST_TEXT_HI,
          letterSpacing: '-0.3px',
          m: 0,
        }}
      >
        {title}
      </Typography>
      {right}
    </Box>
  );
}
